/**
 * Thin glue the React component leans on: resolve a layout, read a physical key
 * event into a stroke, build the text for each mode, and work out the "next key"
 * hint in free mode.
 */
import { bijoyLayout } from './engine/bijoyLayout';
import { englishLayout } from './engine/englishLayout';
import { avroLayout } from './engine/avroLayout';
import { buildLessonText, buildAdaptiveText, wordsForKeys } from './engine/generate';
import { QWERTY, QWERTY_STROKE_OF } from './engine/keyboardGeometry';
import { buildUnits, SPACE_STROKE, sameStroke } from './engine/session';
import { courseFor } from './data/courses';
import { passagesFor } from './data/passages';

export const LAYOUTS = { bijoy: bijoyLayout, avro: avroLayout, english: englishLayout };
export const getLayout = (id) => LAYOUTS[id] || englishLayout;

const ALL_BIJOY_KEYS = 'fjdkhlsagcvbmnxrwtyuioepqVWMJKLHDSFGYRNBOETUIPCXZzAQ|&\\';
const ALL_EN_KEYS = 'abcdefghijklmnopqrstuvwxyz0123456789,.?!;:\'"';
// Avro: every letter, the capitals with a meaning of their own, and its signs.
const ALL_AVRO_KEYS = 'abcdefghijklmnopqrstuvwxyzOIUTDNRSYZG`:^.$0123456789';
const ALL_KEYS = { bijoy: ALL_BIJOY_KEYS, avro: ALL_AVRO_KEYS, english: ALL_EN_KEYS };

/** Every US-QWERTY key unlocked up to and including lesson `i` of a course. */
export function cumulativeKeys(course, i, layoutId) {
    const all = ALL_KEYS[layoutId] || ALL_EN_KEYS;
    const lesson = course[i];
    if (!lesson || lesson.keys === 'ALL') return all;
    // A lesson's own `keys` already lists everything through it (they are cumulative
    // in the course tables), so just return it — plus space is always available.
    return lesson.keys;
}

/** Physical `code`s the current key set lights up (to dim the rest of the board). */
export function activeCodesFor(keys, { caps = false } = {}) {
    const set = new Set(['Space']);
    keys.split('').forEach((ch) => {
        const stroke = QWERTY_STROKE_OF[ch];
        if (stroke) set.add(stroke.code);
        if (caps) {
            const upper = ch.toUpperCase();
            if (upper !== ch && QWERTY_STROKE_OF[upper]) set.add(QWERTY_STROKE_OF[upper].code);
        }
    });
    return set;
}

/**
 * Turn a keydown into a stroke, or a control signal.
 * @returns {{ type: 'stroke', stroke } | { type: 'space' } | { type: 'backspace', whole } | { type:'ime' } | null}
 */
export function readKeyEvent(e) {
    if (e.isComposing || e.keyCode === 229) return { type: 'ime' };
    const { code } = e;
    if (code === 'Space') return { type: 'stroke', stroke: { ...SPACE_STROKE } };
    if (code === 'Backspace') return { type: 'backspace', whole: e.ctrlKey || e.altKey || e.metaKey };
    // Only keys that can carry a character; ignore bare modifiers, arrows, F-keys, Enter, Tab.
    if (!QWERTY[code] || code === 'Space') return null;
    if (e.ctrlKey || e.metaKey || e.altKey) return null; // a shortcut, not typing
    return { type: 'stroke', stroke: { code, shift: e.shiftKey } };
}

/** Text for a given mode. */
export function textForMode({ mode, layoutId, course, lessonIndex, duration, passage, model, adaptiveState, seed }) {
    const layout = getLayout(layoutId);
    if (mode === 'learn') {
        const keys = cumulativeKeys(course, lessonIndex, layoutId);
        const prev = lessonIndex ? cumulativeKeys(course, lessonIndex - 1, layoutId) : '';
        return buildLessonText(layout, course[lessonIndex], keys, prev, seed);
    }
    if (mode === 'practice') {
        const included = adaptiveState.included.join('');
        return buildAdaptiveText(layout, included, adaptiveState.focus, { words: 40, seed });
    }
    if (mode === 'test') {
        // Enough words to outlast the longest duration at a fast pace.
        const pool = wordsForKeys(layout, ALL_KEYS[layoutId] || ALL_EN_KEYS, { limit: 400 });
        const want = Math.max(80, Math.round((duration / 60) * 90));
        const out = [];
        let s = seed;
        while (out.length < want && pool.length) {
            s = (s * 1103515245 + 12345) & 0x7fffffff;
            out.push(pool[s % pool.length]);
        }
        return out.join(' ');
    }
    // Avro types plain quotes and no joiners, so the passage is folded to what it can type.
    if (mode === 'exam') return layout.phonetic ? layout.normalizeText(passage?.text) : (passage?.text || '');
    return '';
}

/** Build the units for a strict text (learn / practice). */
export const unitsFor = (layoutId, text) => buildUnits(getLayout(layoutId), text);

/**
 * Next stroke to glow in free mode: encode the current word and read off the key at
 * the position the typist has reached, when what they typed is still a valid prefix.
 */
export function freeHint(session, layout) {
    const i = session.index;
    const word = session.words[i];
    if (!word) return null;
    const units = layout.encodeWord(word);
    const seq = units.flatMap((u) => u.options[0]);
    const typedCount = session.buffers[i].length;
    if (typedCount >= seq.length) return null;
    return seq[typedCount] || null;
}

/**
 * Whether the keys typed so far for the current free-mode word are still on a
 * correct path to it — judged by keystrokes against every valid key sequence of
 * the word, not by the rendered text (a Bijoy pre-base ি typed first does not
 * look like the start of "বি" yet, but it is the right key).
 */
export function freeWordOnTrack(session, layout) {
    const i = session.index;
    const word = session.words[i];
    const typed = session.buffers[i] || [];
    if (!word) return !typed.length;
    if (!typed.length) return true;
    const units = layout.encodeWord(word);
    const fits = (unit, pos) => {
        if (pos >= typed.length) return true; // everything typed matched: a valid prefix
        if (unit >= units.length) return false; // typed past the end of the word
        return units[unit].options.some((option) => {
            for (let k = 0; k < option.length; k += 1) {
                if (pos + k >= typed.length) return true; // ends inside this unit
                if (!sameStroke(option[k], typed[pos + k])) return false;
            }
            return fits(unit + 1, pos + option.length);
        });
    };
    if (fits(0, 0)) return true;
    // Fallback for spellings the encoder does not list: the rendered text is a prefix.
    const shown = (session.rendered[i] || '').normalize('NFC');
    return !!shown && word.normalize('NFC').startsWith(shown);
}

export { courseFor, passagesFor };
